/*
 * One message in the timeline (spec 12 §4.3, UX §5.3–§5.5). Five decisions live here.
 *
 * The row is a two-column grid: the gutter that carries the author's picture and, on a
 * continuation row, that message's own time; and the text column, which everything else shares —
 * body, attachments, thread footer, reaction chips. Consecutive messages by the same author drop
 * the header so a burst reads as one utterance, but every row keeps its own `<time datetime>`,
 * because the header is chrome and the time is information: on a continuation row it is in the DOM
 * at every moment and only *painted* when the row is hovered or focused. Whether a row continues
 * the one above it is not decided here — `buildRows` decides it for the list as a whole, because a
 * divider between two messages breaks the block and this component never sees the divider.
 *
 * The hover bar is a `role="toolbar"`: one tab stop for the whole bar, the arrow keys inside it,
 * every button but the current one out of the tab sequence — the pattern `core/editor/Toolbar.tsx`
 * uses for the formatting row. That keeps P18's win (one stop per row, not seven) and still lets a
 * keyboard reach the one-click reaction a mouse gets. The stop the bar opens on is the menu,
 * because the menu is everything the row can do and the two other paths into it — the quick emoji
 * and *Reply in thread* — are shortcuts rather than the only way to anything. The bar is also the
 * **last** child of the article, so the row is read author → time → body → toolbar and an arriving
 * message is announced as its content rather than as six control names (X-A2 D4, P12). Nothing in
 * it is *created* by hover either ([25] B1): it is in the DOM and at full size at every moment, and
 * hover only paints it. It floats as `.tb-elevated`, the shared recipe, which is put on rather than
 * painted over (visual-language.md).
 *
 * The bar exists only where a pointer can hover. Where one cannot, the row draws **no** control: two
 * 44 px buttons standing permanently in every row's corner cover a quarter of a short row, so a press
 * held on the row's body opens the same actions as a sheet instead (ActionSheet.tsx), filled from the
 * same `menuItems()` list. The question is the pointer's and not the window's — a mouse in a narrow
 * window keeps its bar, and plugging one into a tablet brings the bar back under the reader's hand —
 * and where the bar is gone the emoji picker hangs from a point in the row rather than from a button,
 * because the reaction row's `+` chip and the sheet both still open it.
 *
 * An affordance whose caller is missing is not drawn: the author's name is a button only while a
 * surface passed `openProfile`, and the thread footer and the *Reply in thread* entries exist only
 * while it passed `openThread`. A control that cannot do anything is worse than none, and it is
 * what makes this file safe to land before the surfaces that call it.
 *
 * A pinned message carries a line above its header — a 12 px pin and who pinned it — and its menu
 * offers *Unpin* where an unpinned one offers *Pin to this conversation*: the pin is read from the
 * conversation's own list (`conversation/pins.ts`), never from the message, so the line and the
 * pinned list cannot disagree.
 *
 * The emoji picker (core/emoji/EmojiPicker) is one popover per row, hung from the hover bar's own
 * button: the button opens it, and so does *Add a reaction* in the menu or the sheet and the `+` chip of the
 * reaction row, through the open state this row owns. *Forward …* is the one menu entry whose dialog the row
 * mounts itself: what it needs is the message and the author's name, and both are already here, so
 * nothing about forwarding reaches the surfaces that draw rows (X-A4 N11).
 *
 * A deleted message is a tombstone and keeps little but its place: no picture, no name, one muted
 * line at the body's indent, with the seq, the `<time datetime>` and the dividers around it
 * untouched, and a bar reduced to *Copy link* — the one thing a reader still does with a message
 * that is gone (X-A1 P10). The name above a stub reads as an accusation and is the one row where
 * it adds nothing (X-A3 C19, X-A2 D10); what the row is, and when, is in its `aria-label` instead.
 *
 * A system row carries no markdown — its story is `kind` plus the ids in `meta` — so its sentence is
 * composed by systemText.ts in the reader's language (spec 05 §2.6), with an icon where a person's
 * picture would be. A row of a kind this build does not know is not drawn at all.
 */

import {
	memo,
	useCallback,
	useRef,
	useState,
	useSyncExternalStore,
	type ReactNode,
	type RefObject,
} from "react"
import { EmojiPicker } from "../../../core/emoji/EmojiPicker.tsx"
import { useEmojiPrefs } from "../../../core/emoji/recents.ts"
import { groupName } from "../../../core/groups/group.ts"
import { formatDate, formatDateTime, formatTime, isoDateTime } from "../../../core/i18n/format.ts"
import { useIsActiveRow } from "../../../core/list/activeRow.ts"
import { Avatar } from "../../../core/people/Avatar.tsx"
import { PersonFace, PersonName, type ChipPerson } from "../../../core/people/PersonChip.tsx"
import { isAdmin } from "../../../core/people/standing.ts"
import { useRenderContext } from "../../../core/render/context.ts"
import { leadsBesideHeader, MarkdownBody } from "../../../core/render/MarkdownBody.tsx"
import { Icon, useToast } from "../../../core/shell/index.ts"
import { useModel } from "../../../core/store/store.ts"
import { cx } from "../../../lib/cx.ts"
import { Button, Menu, type MenuItem } from "../../../ui/index.ts"
import { AttachmentList } from "../attachments/index.ts"
import { setPinned } from "../conversation/api.ts"
import { DeleteConfirm } from "../conversation/DeleteConfirm.tsx"
import { usePinOf } from "../conversation/pins.ts"
import { t } from "../i18n/t.ts"
import { canReact, toggleReaction } from "../reactions/deps.ts"
import { ReactionBar } from "../reactions/ReactionBar.tsx"
import "../render/chat.css"
import { QuotedMessages } from "../render/QuotedMessage.tsx"
import { conversationMeta, isDirect } from "../state/selectors.ts"
import type { MessageView } from "../wire/views.ts"
import { ActionSheet, useActionGesture } from "./ActionSheet.tsx"
import { deleteMessageAsAdmin } from "./adminApi.ts"
import { ForwardDialog } from "./ForwardDialog.tsx"
import { systemText } from "./systemText.ts"

export interface MessageRowActions {
	copyLink: (msg: MessageView) => void
	copyMarkdown: (msg: MessageView) => void
	edit: (msg: MessageView) => void
	remove: (msg: MessageView) => void
	markUnreadFrom: (msg: MessageView) => void
	toggleSelect: (msg: MessageView) => void
	/** Opens the message's thread; absent while no surface can show one. */
	openThread?: ((msg: MessageView) => void) | undefined
	/** Opens a person's profile; the author's name and picture are its two triggers. */
	openProfile?: ((userId: number) => void) | undefined
	/**
	 * The reader holds no row in this conversation — a channel read through one of its groups, or
	 * an administrator outside it. The server refuses every write of theirs there, so no reaction,
	 * reply or pin is offered: a control that is only ever refused, silently for a reaction, is a lie.
	 */
	readOnly?: boolean | undefined
}

/**
 * A message on its way out, drawn in the row's own shape. The state decides the tone and the
 * status line says what is holding it up; both come from the outbox (core/drafts), which is where
 * the sending model lives — this component only draws what it is handed.
 */
export interface PendingInfo {
	state: "queued" | "held" | "sending" | "failed"
	statusLine: ReactNode
}

export interface MessageRowProps {
	msg: MessageView
	/** True while this row joins the block above it: no header, no repeated name (`buildRows`). */
	continues: boolean
	/**
	 * True on a continuation row that follows a silence long enough to be worth an anchor
	 * (`buildRows`'s `timeGap`): its gutter time is then painted at every moment instead of only
	 * under the pointer, so a dense morning is not one wall of text (X-A1 P15).
	 */
	showTime?: boolean | undefined
	authorName: string
	/** True while the signed-in person wrote it: only then are edit and delete offered. */
	mine: boolean
	selected: boolean
	actions: MessageRowActions
	/** True when the message names the reader — by handle, through a group, or with @channel. */
	mentionsMe?: boolean | undefined
	/** True while the thread carries replies this reader has not seen. */
	threadUnread?: boolean | undefined
	/** Set by a surface that is already inside the thread: the footer would point at itself. */
	hideThreadFooter?: boolean | undefined
	/** Present only on an unacknowledged send; the row is then a preview of one. */
	pending?: PendingInfo | undefined
}

/** The three quick reactions offered before this account has used any emoji of its own. */
const FALLBACK_QUICK = ["👍", "✅", "👀"]

/**
 * How many faces the thread footer shows, oldest reply first (§4.5). Three 24 px faces with two
 * letters each are readable; the five 20 px faces the footer used to carry were a smear of colour
 * nobody could read a name off (D-05 D1). Everybody past the third is one "+n" slot.
 */
const FOOTER_FACES = 3

/**
 * Whether the pointer can hover, which is what decides whether a row draws a bar at all or answers a
 * press with the sheet. `chat.css` asks the same question for the paints that go with each, and a
 * document answers it once: one MediaQueryList is shared by every row, because three hundred rows
 * asking `matchMedia` is three hundred lists to subscribe to. The list is remade when the function
 * behind it changes — which is what a test renderer does when it stubs the global — and the answer is
 * "can hover" where there is no `matchMedia` at all, so the bar's contract stays measurable there.
 */
const HOVER_QUERY = "(hover: hover)"
let hoverMedia: { asked: unknown; media: MediaQueryList | undefined } | undefined

function hoverQuery(): MediaQueryList | undefined {
	const ask = globalThis.matchMedia
	if (hoverMedia === undefined || hoverMedia.asked !== ask) {
		hoverMedia = { asked: ask, media: ask?.(HOVER_QUERY) }
	}
	return hoverMedia.media
}

function subscribeHover(onChange: () => void): () => void {
	const media = hoverQuery()
	if (media === undefined) return () => undefined
	media.addEventListener("change", onChange)
	return () => media.removeEventListener("change", onChange)
}

function canHoverNow(): boolean {
	return hoverQuery()?.matches ?? true
}

function useCanHover(): boolean {
	return useSyncExternalStore(subscribeHover, canHoverNow, () => true)
}

/**
 * Opens the action sheet, with the row taking the focus first. A press leaves the focus where it was
 * — on a touch screen that is the document's body — and `ui/Dialog` hands the focus back to whatever
 * held it when the sheet opened, so this is what decides that a reader comes back to the message they
 * pressed rather than to the top of the page. The row is focusable for this and for nothing else:
 * `tabIndex={-1}` is a target for `.focus()` and not a stop in the Tab sequence.
 */
function openSheetFrom(row: RefObject<HTMLElement | null>, open: (next: boolean) => void): void {
	row.current?.focus({ preventScroll: true })
	open(true)
}

function Timestamp({ at, gutter, shown }: { at: number; gutter?: boolean; shown?: boolean }) {
	return (
		<time
			className={cx(
				"tb-msg__time",
				gutter === true && "tb-msg__time--gutter",
				shown === true && "tb-msg__time--shown",
			)}
			dateTime={isoDateTime(at)}
			title={formatDateTime(at)}
		>
			{formatTime(at)}
		</time>
	)
}

/** The line above a pinned message: a small pin and who put it there, in words (D-10 P4). */
function PinnedLine({ by }: { by: string }) {
	return (
		<p className="tb-msg__pinned">
			<Icon name="pin" size={12} />
			{t("chat.message.pinnedBy", { name: by })}
		</p>
	)
}

function SystemRow({ msg }: { msg: MessageView }) {
	const { names } = useRenderContext()
	const groups = useModel((model) => model.groups)
	// A group is named as the reader's model names it; one the model does not know is gone.
	const nameOfGroup = (id: number): string => {
		const group = groups[id]
		return group === undefined ? t("chat.system.goneGroup") : groupName(group)
	}
	// The one kind worded by its place (`agents_changed`) asks whether the place is a direct
	// conversation; a row of a conversation the model no longer holds reads as a channel's.
	const direct = useModel((model) => isDirect(conversationMeta(model, msg.conv)?.kind ?? ""))
	return (
		<SystemLine msg={msg}>
			{systemText(msg, (id) => names[id] ?? "", nameOfGroup, direct)}
		</SystemLine>
	)
}

/** The shape every system row has: the icon where a picture would be, one line, the time. */
function SystemLine({ msg, children }: { msg: MessageView; children: ReactNode }) {
	return (
		<div className="tb-msg tb-msg--system">
			<span className="tb-msg__gutter">
				<Icon name="info" size={16} />
			</span>
			<span className="tb-msg__main">
				<span className="tb-msg__system">{children}</span>
				<Timestamp at={msg.createdAt} />
			</span>
		</div>
	)
}

/**
 * The button under a root message that opens its thread. Its accessible name is the whole sentence
 * — "4 replies, last reply 14:32" — because a count on its own says nothing about what is behind
 * it, and an icon alone says even less (spec 05 §4). The faces and the "+n" are decoration for the
 * eye; the sentence is the name.
 */
function ThreadFooter({
	msg,
	unread,
	active,
	onOpen,
}: {
	msg: MessageView
	unread: boolean
	active: boolean
	onOpen: (msg: MessageView) => void
}) {
	const { avatars, names } = useRenderContext()
	// The clock is read once, when the row is first drawn: "14:32" and "6 September" are the same
	// sentence for the whole life of a row, and a render that answered differently each time would
	// be a footer that changed under a reader who did nothing.
	const [now] = useState(() => Date.now())
	const count = msg.replyCount ?? 0
	if (count === 0) return null

	const at = msg.lastReplyAt ?? msg.createdAt
	const when = formatDate(at) === formatDate(now) ? formatTime(at) : formatDate(at)
	const repliers = msg.replyUserIds ?? []
	const faces = repliers.slice(0, FOOTER_FACES)
	const more = repliers.length - faces.length
	return (
		<button
			type="button"
			className={cx("tb-msg__thread", unread && "tb-msg__thread--unread")}
			aria-label={
				unread
					? t("chat.thread.footerUnread", { count, when })
					: t("chat.thread.footer", { count, when })
			}
			tabIndex={active ? 0 : -1}
			onClick={() => onOpen(msg)}
		>
			{faces.length === 0 ? null : (
				<span className="tb-msg__thread-faces">
					{faces.map((id) => (
						<span key={id} className="tb-msg__thread-face">
							<Avatar name={names[id] ?? ""} id={id} size="sm" url={avatars?.[id]} />
						</span>
					))}
					{more > 0 ? (
						<span className="tb-msg__thread-face tb-msg__thread-more" aria-hidden="true">
							{t("chat.msg.moreRepliers", { count: more })}
						</span>
					) : null}
				</span>
			)}
			<span className="tb-msg__thread-count">{t("chat.thread.replies", { count })}</span>
			<span className="tb-msg__thread-when">
				{`${t("chat.thread.lastReply")} `}
				<time dateTime={isoDateTime(at)} title={formatDateTime(at)}>
					{when}
				</time>
			</span>
		</button>
	)
}

/**
 * The row's whole menu, in the order a reader looks for it: react and reply, then the two that
 * change the message, then the three that take it somewhere else — as markdown, as a link, or into
 * another conversation — then the two that change what *this* reader sees of it. Every entry carries
 * the icon its counterpart in the hover bar and in the toolbar wears, so a reader recognises the
 * action rather than reading eight lines of text (X-A3 P12). The two copies wear `copy` — `link` is
 * a hyperlink's glyph and would say "open" where the entry means "take" (D-04 D4-15) — and the two
 * deletes wear `trash`.
 *
 * The administrator's delete is last and under the separator, because it is the one entry that acts
 * on somebody else's message. It is offered only where it can be used — an administrator, a live
 * message, not their own, which is what the author's own *Delete* is for.
 */
function menuItems(
	msg: MessageView,
	mine: boolean,
	deleted: boolean,
	selected: boolean,
	actions: MessageRowActions,
	onReact: () => void,
	pin: { pinned: boolean; toggle: () => void } | undefined,
	onForward: (() => void) | undefined,
	onAdminDelete: (() => void) | undefined,
): MenuItem[] {
	const items: MenuItem[] = []
	if (actions.readOnly !== true) {
		items.push({
			id: "react",
			icon: "emoji",
			label: t("chat.reaction.pick"),
			disabled: deleted || !canReact(),
			onSelect: onReact,
		})
	}
	// A reply of a thread has no thread of its own, and a tombstone answers nothing (§4.5).
	if (
		actions.openThread &&
		actions.readOnly !== true &&
		!deleted &&
		msg.threadRootId === undefined
	) {
		const openThread = actions.openThread
		items.push({
			id: "thread",
			icon: "thread",
			label: t("chat.thread.reply"),
			onSelect: () => openThread(msg),
		})
	}
	// A pin is offered on every live message, one's own or anybody's (X-A4 N1).
	if (pin !== undefined) {
		items.push({
			id: "pin",
			icon: "pin",
			label: pin.pinned ? t("chat.message.unpin") : t("chat.message.pin"),
			onSelect: pin.toggle,
		})
	}
	if (mine && !deleted) {
		items.push({
			id: "edit",
			icon: "edit",
			label: t("chat.message.edit"),
			onSelect: () => actions.edit(msg),
		})
	}
	items.push(
		{
			id: "copyMarkdown",
			icon: "copy",
			label: t("chat.message.copyMarkdown"),
			onSelect: () => actions.copyMarkdown(msg),
			separatorBefore: true,
		},
		{
			id: "copyLink",
			icon: "copy",
			label: t("chat.message.copyLink"),
			onSelect: () => actions.copyLink(msg),
		},
	)
	// Forwarding writes a draft and navigates, so it is offered where there is something to quote: a
	// tombstone has no body and a message on its way out has no permalink yet.
	if (onForward !== undefined) {
		items.push({
			id: "forward",
			icon: "forward",
			label: t("chat.forward.action"),
			onSelect: onForward,
		})
	}
	items.push(
		{
			id: "markUnread",
			icon: "eyeOff",
			label: t("chat.message.markUnreadFrom"),
			onSelect: () => actions.markUnreadFrom(msg),
			separatorBefore: true,
		},
		{
			id: "select",
			icon: "check",
			label: selected ? t("chat.message.clearSelection") : t("chat.message.select"),
			onSelect: () => actions.toggleSelect(msg),
		},
	)
	if (mine && !deleted) {
		items.push({
			id: "delete",
			icon: "trash",
			label: t("chat.message.delete"),
			danger: true,
			onSelect: () => actions.remove(msg),
		})
	}
	if (onAdminDelete !== undefined) {
		items.push({
			id: "adminDelete",
			icon: "trash",
			label: t("chat.message.deleteAsAdmin"),
			danger: true,
			separatorBefore: true,
			onSelect: onAdminDelete,
		})
	}
	return items
}

/**
 * The tombstone: what is left of a deleted message. No picture, no name — the row's `aria-label`
 * says what it is and when it was written, and the line itself sits at the body's indent so the
 * column of text is unbroken. The seq and the `<time datetime>` stay, because they are what a
 * permalink and a machine read; the replies of a thread whose root was deleted outlive it, so the
 * footer that reaches them stays too. Its bar is *Copy link* alone: a permalink still answers, and
 * nothing else the bar offers has a message to act on — and so is its sheet, where the press is how a
 * row is asked.
 */
function TombstoneRow({
	msg,
	continues,
	threadUnread,
	hideThreadFooter,
	actions,
}: MessageRowProps) {
	const active = useIsActiveRow(msg.seq)
	const canHover = useCanHover()
	const row = useRef<HTMLElement>(null)
	const [sheet, setSheet] = useState(false)
	const gesture = useActionGesture(!canHover, () => openSheetFrom(row, setSheet))
	const openThread = actions.openThread
	const showFooter =
		openThread !== undefined && msg.threadRootId === undefined && hideThreadFooter !== true
	return (
		<article
			ref={row}
			className={cx("tb-msg", continues ? "tb-msg--cont" : "tb-msg--head", "tb-msg--deleted")}
			aria-label={t("chat.message.deletedRow", { time: formatTime(msg.createdAt) })}
			{...(canHover ? {} : { tabIndex: -1 })}
			{...gesture}
		>
			<div className="tb-msg__gutter" />
			<div className="tb-msg__main">
				<p className="tb-msg__tombstone">
					{msg.byAdmin ? t("chat.message.deletedByAdmin") : t("chat.message.deleted")}
					<Timestamp at={msg.createdAt} />
				</p>
				{showFooter ? (
					<ThreadFooter
						msg={msg}
						unread={threadUnread === true}
						active={active}
						onOpen={openThread}
					/>
				) : null}
			</div>
			{canHover ? (
				<div
					className="tb-elevated tb-msg__actions"
					role="toolbar"
					aria-label={t("chat.message.quickActions")}
				>
					<Button
						variant="ghost"
						size="sm"
						quiet
						icon
						className="tb-msg__action"
						aria-label={t("chat.message.copyLink")}
						before={<Icon name="copy" />}
						tabIndex={active ? 0 : -1}
						onClick={() => actions.copyLink(msg)}
					/>
				</div>
			) : null}
			{sheet ? (
				<ActionSheet
					open
					onOpenChange={setSheet}
					about={t("chat.message.deletedRow", { time: formatTime(msg.createdAt) })}
					items={[
						{
							id: "copyLink",
							icon: "copy",
							label: t("chat.message.copyLink"),
							onSelect: () => actions.copyLink(msg),
						},
					]}
				/>
			) : null}
		</article>
	)
}

function UserRow(props: MessageRowProps) {
	const {
		msg,
		continues,
		showTime,
		authorName,
		selected,
		mine,
		actions,
		mentionsMe,
		threadUnread,
		hideThreadFooter,
		pending,
	} = props
	const active = useIsActiveRow(msg.seq)
	const canHover = useCanHover()
	// The one thing the row reads out of the store: whether this reader may remove somebody else's
	// message. It is a boolean selector, so a row re-renders only when the answer changes — never
	// when the model does.
	const readerIsAdmin = useModel((model) => isAdmin(model.me))
	// The picker's open state is the row's, because three doors open the one panel: its own button
	// in the bar, *Add a reaction* in the menu and the `+` chip of the reaction row. The menu's door
	// opens the panel only once the menu has closed: a popover that opens while the menu is still
	// handing the focus back to its trigger sees that as a focus outside itself and dismisses at once.
	const [picking, setPicking] = useState(false)
	const [pickAfterMenu, setPickAfterMenu] = useState(false)
	// The bar's roving stop. It opens on the menu — the entry that holds everything the row can do —
	// and the arrow keys walk from there into the shortcuts beside it.
	const [stop, setStop] = useState("menu")
	const [confirmAdminDelete, setConfirmAdminDelete] = useState(false)
	const [forwarding, setForwarding] = useState(false)
	const [adminError, setAdminError] = useState("")
	const [pinError, setPinError] = useState("")
	// Where a pointer that cannot hover asks for the same actions: a press held on the row's body.
	const [sheet, setSheet] = useState(false)
	const pin = usePinOf(msg.conv, msg.id)
	const { avatars, names } = useRenderContext()
	const rowRef = useRef<HTMLElement>(null)
	const barRef = useRef<HTMLDivElement>(null)
	const gesture = useActionGesture(!canHover && props.pending === undefined, () =>
		openSheetFrom(rowRef, setSheet),
	)
	const { recents } = useEmojiPrefs()
	const toast = useToast()

	const deleted = msg.deletedAt !== undefined
	const readOnly = actions.readOnly === true
	const enabled = canReact() && !readOnly && !deleted && pending === undefined

	const pick = useCallback((emoji: string) => toggleReaction(msg.id, emoji, true), [msg.id])

	// Pin and unpin are one call with the state flipped; the row on screen follows the event the
	// server publishes, so a refusal only has to be said, never undone.
	const pinned = pin !== undefined
	const togglePin = useCallback(() => {
		setPinError("")
		setPinned(msg.id, !pinned)
			.then(() =>
				toast({ kind: "success", key: pinned ? "chat.message.unpinned" : "chat.message.pinned" }),
			)
			.catch(() => setPinError(t("chat.pin.failed")))
	}, [msg.id, pinned, toast])

	const removeAsAdmin = useCallback(() => {
		setConfirmAdminDelete(false)
		setAdminError("")
		deleteMessageAsAdmin(msg.id).catch(() => setAdminError(t("chat.message.deleteFailed")))
	}, [msg.id])

	const quick = (recents.length > 0 ? recents : FALLBACK_QUICK).slice(0, 3)
	const openProfile = actions.openProfile
	const openThread = actions.openThread
	// The person this row is by, as every surface of the product describes one
	// (core/people/PersonChip.tsx): the face, the name, the hover card and the presence dot are one
	// decision. The maps are what a row has instead of an account row; the card looks the rest up.
	const author: ChipPerson = {
		id: msg.authorId ?? 0,
		name: authorName,
		avatarUrl: avatars?.[msg.authorId ?? 0],
	}
	// A reply has no thread of its own, so neither the action nor the footer is offered on one. The
	// action needs a live message as well; the footer stays on a tombstone, because the replies it
	// counts outlive the root (§4.5).
	const inThread = openThread !== undefined && msg.threadRootId === undefined
	const canReply = inThread && !readOnly && !deleted && pending === undefined
	const canPin = !readOnly && !deleted && pending === undefined
	// There is nothing to quote of a tombstone, and a message still on its way out has no permalink.
	const canForward = !deleted && pending === undefined

	// The bar's stops in DOM order. The ids are positional rather than the emoji themselves: two
	// recents that are the same character would otherwise be one stop.
	const stops = [
		...(readOnly ? [] : quick.map((_, index) => `quick${String(index)}`)),
		...(readOnly ? [] : ["add"]),
		...(canReply ? ["thread"] : []),
		"menu",
	]
	const current = stops.includes(stop) ? stop : "menu"
	const barStop = (id: string) => (active && id === current ? 0 : -1)
	const moveTo = (id: string) => {
		setStop(id)
		barRef.current?.querySelector<HTMLElement>(`[data-stop="${id}"]`)?.focus()
	}
	/*
	 * The arrow keys inside the bar, in the capture phase on purpose: the scroller's own key
	 * listener (WindowedList) claims Home and End for the roving walk over rows, and it reads
	 * `defaultPrevented` on a native listener that runs before React's bubbled handlers would. A
	 * capture handler prevents the default early enough for the list to leave these keys alone.
	 *
	 * Which is also why the target is checked first. The emoji picker opens in a portal, and a
	 * portal is a React child of the element it was rendered from however far away it lands in the
	 * document: without this guard the grid's own ArrowRight would be caught here, before the grid
	 * ever saw it, and walking the emoji would walk the bar instead.
	 */
	const onBarKeyDown = (event: React.KeyboardEvent) => {
		if (!(event.target instanceof Node) || barRef.current?.contains(event.target) !== true) return
		const index = stops.indexOf(current)
		let next: string | undefined
		switch (event.key) {
			case "ArrowRight":
				next = stops[(index + 1) % stops.length]
				break
			case "ArrowLeft":
				next = stops[(index - 1 + stops.length) % stops.length]
				break
			case "Home":
				next = stops[0]
				break
			case "End":
				next = stops[stops.length - 1]
				break
			default:
				return
		}
		if (next === undefined) return
		event.preventDefault()
		event.stopPropagation()
		moveTo(next)
	}

	if (deleted) return <TombstoneRow {...props} />

	// "(edited)" belongs to the end of the body's last line, not to a line of its own — which is
	// also why it stands before the attachments and not after them (X-A1 P8, D-05 D3).
	//
	// A zero is "no visible edit", not "edited at the epoch": a correction inside the five-minute
	// grace window collapses, and the event that carries it says so by leaving `editedAt` at 0
	// (`chat.gen.ts`, `editing/policy.go`). Read as "present" it drew the marker on a message nobody
	// had visibly edited, with "1 Jan 1970, 01:00" in its tooltip (measured on K=34).
	const edited =
		msg.editedAt === undefined || msg.editedAt === 0 ? undefined : (
			<span className="tb-msg__edited" title={formatDateTime(msg.editedAt)}>
				{t("chat.message.edited")}
			</span>
		)

	const about = t("chat.message.from", {
		author: authorName,
		time: formatDateTime(msg.createdAt),
	})

	/**
	 * The row's one picker, wherever it hangs from: the bar's own button where there is a bar, and a
	 * point at the row's end edge where there is none. Four doors open the same panel — the button,
	 * *Add a reaction* in the menu and in the sheet, and the `+` chip of the reaction row — so there is
	 * one open state and one `onPick`, and the anchor is the only thing that changes.
	 */
	const picker = (trigger: ReactNode) => (
		<EmojiPicker
			label={t("chat.reaction.pick")}
			open={picking}
			onOpenChange={setPicking}
			onPick={pick}
			trigger={trigger}
		/>
	)

	return (
		<article
			ref={rowRef}
			className={cx(
				"tb-msg",
				continues ? "tb-msg--cont" : "tb-msg--head",
				selected && "tb-msg--selected",
				mentionsMe === true && "tb-msg--mentions-me",
				pending !== undefined && "tb-msg--pending",
				// Whether the body's first block shares the line the header stands on. It is the
				// compact density's one question about a body — its header is a float, and the dot
				// after the time separates the time from words rather than from a list below it.
				leadsBesideHeader(msg.render, msg.markdown) && "tb-msg--lead-inline",
			)}
			aria-label={about}
			{...(canHover ? {} : { tabIndex: -1 })}
			{...gesture}
		>
			<div className="tb-msg__gutter">
				{continues ? (
					<Timestamp at={msg.createdAt} gutter shown={showTime === true} />
				) : (
					/* The face keeps the row's own class — chat.css sizes the gutter and the compact
					   density follows it — and draws nothing of its own. Without an opener it is the
					   picture alone, exactly as before. */
					<PersonFace
						person={author}
						size="md"
						className="tb-msg__face"
						tabIndex={-1}
						{...(msg.authorId === undefined ? {} : { onOpen: openProfile })}
					/>
				)}
			</div>

			<div className="tb-msg__main">
				{pin === undefined ? null : <PinnedLine by={names[pin.byUserId] ?? ""} />}
				{continues ? null : (
					<header className="tb-msg__header">
						<PersonName
							person={author}
							className="tb-msg__author"
							tabIndex={active ? 0 : -1}
							{...(msg.authorId === undefined ? {} : { onOpen: openProfile })}
						/>
						<Timestamp at={msg.createdAt} />
					</header>
				)}
				<div className="tb-msg__body">
					<MarkdownBody render={msg.render} source={msg.markdown} trailing={edited} />
					<AttachmentList attachments={msg.attachments ?? []} />
				</div>

				{/* A teamBase permalink in the body draws the message it names, under the body and never
				    inside it: a card is a block and the link is a word in a sentence (X-A4 N10b). The row
				    is the mount point rather than `MarkdownBody`, because the tree is also rendered where
				    a card would be wrong — the composer's preview and the clipboard's HTML flavour. */}
				{pending === undefined ? (
					<QuotedMessages nodes={msg.render?.nodes ?? []} active={active} />
				) : null}

				{pending === undefined ? null : <p className="tb-msg__state">{pending.statusLine}</p>}
				{adminError === "" ? null : (
					<p className="tb-msg__state" role="alert">
						{adminError}
					</p>
				)}
				{pinError === "" ? null : (
					<p className="tb-msg__state" role="alert">
						{pinError}
					</p>
				)}

				{inThread && hideThreadFooter !== true && pending === undefined ? (
					<ThreadFooter
						msg={msg}
						unread={threadUnread === true}
						active={active}
						onOpen={openThread}
					/>
				) : null}

				{pending === undefined ? (
					<ReactionBar
						messageId={msg.id}
						reactions={msg.reactions ?? []}
						active={active}
						onAdd={enabled ? () => setPicking(true) : undefined}
						readOnly={readOnly}
					/>
				) : null}
			</div>

			{/* Last in the row's DOM, which is what makes the row read author → time → body → actions
			    (X-A2 D4). It is painted 8 px above the row's top edge, into the block gap, and it is
			    one tab stop for the whole bar, opened on the menu, walked with ←/→. Six buttons on the
			    dense rung: the three recent emoji, the picker, the reply, the menu. Where the pointer
			    cannot hover there is no bar at all and the press opens the sheet below — and the panel
			    the `+` chip and the sheet still open needs somewhere to hang from, which is the point
			    the bar stood at. It is no control: nothing draws, nothing focuses, and the
			    accessibility tree does not see it. */}
			{pending !== undefined ? null : !canHover ? (
				picker(<span className="tb-msg__pick-anchor" aria-hidden="true" />)
			) : (
				<div
					ref={barRef}
					className="tb-elevated tb-msg__actions"
					role="toolbar"
					aria-label={t("chat.message.quickActions")}
					onKeyDownCapture={onBarKeyDown}
				>
					{(readOnly ? [] : quick).map((emoji, index) => (
						<Button
							key={`${String(index)}:${emoji}`}
							variant="ghost"
							size="sm"
							quiet
							icon
							className="tb-msg__quick"
							data-stop={`quick${String(index)}`}
							aria-label={t("chat.msg.reactWith", { emoji })}
							label={emoji}
							tabIndex={barStop(`quick${String(index)}`)}
							disabled={!enabled}
							onFocus={() => setStop(`quick${String(index)}`)}
							onClick={() => toggleReaction(msg.id, emoji, true)}
						/>
					))}
					{readOnly ? null : (
						<>
							<span className="tb-msg__actions-sep" aria-hidden="true" />
							{picker(
								<Button
									variant="ghost"
									size="sm"
									quiet
									icon
									className="tb-msg__action"
									data-stop="add"
									aria-label={t("chat.reaction.pick")}
									before={<Icon name="emoji" />}
									tabIndex={barStop("add")}
									disabled={!enabled}
									onFocus={() => setStop("add")}
								/>,
							)}
						</>
					)}
					{canReply ? (
						<Button
							variant="ghost"
							size="sm"
							quiet
							icon
							className="tb-msg__action"
							data-stop="thread"
							aria-label={t("chat.thread.reply")}
							before={<Icon name="thread" />}
							tabIndex={barStop("thread")}
							onFocus={() => setStop("thread")}
							onClick={() => openThread(msg)}
						/>
					) : null}
					<Menu
						onCloseAutoFocus={(event) => {
							if (!pickAfterMenu) return
							setPickAfterMenu(false)
							// The panel takes the focus the menu would have given back to the `⋯`.
							event.preventDefault()
							setPicking(true)
						}}
						trigger={
							<Button
								variant="ghost"
								size="sm"
								quiet
								icon
								className="tb-msg__menu"
								data-stop="menu"
								aria-label={t("chat.message.actions")}
								before={<Icon name="more" />}
								tabIndex={barStop("menu")}
								onFocus={() => setStop("menu")}
							/>
						}
						items={menuItems(
							msg,
							mine,
							deleted,
							selected,
							actions,
							() => setPickAfterMenu(true),
							canPin ? { pinned, toggle: togglePin } : undefined,
							canForward ? () => setForwarding(true) : undefined,
							readerIsAdmin && !mine ? () => setConfirmAdminDelete(true) : undefined,
						)}
					/>
				</div>
			)}

			{/* Mounted only while it is open, like the delete confirm under it: three hundred rows
			    must not each hold a closed dialog. The row is also the one place that knows the
			    author's name, which is what the quotation is headed with. */}
			{forwarding ? (
				<ForwardDialog msg={msg} authorName={authorName} onClose={() => setForwarding(false)} />
			) : null}

			{confirmAdminDelete ? (
				<DeleteConfirm
					message={msg}
					asAdmin
					onCancel={() => setConfirmAdminDelete(false)}
					onConfirm={removeAsAdmin}
				/>
			) : null}

			{/* The same entries as the menu, in the same order, under the three one-tap reactions the
			    bar would have offered. *Add a reaction* opens the picker here and now rather than after a
			    close, because the sheet runs what was chosen only once it has left the document. */}
			{sheet ? (
				<ActionSheet
					open
					onOpenChange={setSheet}
					about={about}
					{...(enabled ? { quick, onQuick: pick } : {})}
					items={menuItems(
						msg,
						mine,
						deleted,
						selected,
						actions,
						() => setPicking(true),
						canPin ? { pinned, toggle: togglePin } : undefined,
						canForward ? () => setForwarding(true) : undefined,
						readerIsAdmin && !mine ? () => setConfirmAdminDelete(true) : undefined,
					)}
				/>
			) : null}
		</article>
	)
}

function MessageRowImpl(props: MessageRowProps) {
	if (props.msg.authorKind === "system") return <SystemRow msg={props.msg} />
	return <UserRow {...props} />
}

/**
 * Memoised on the message object: the reducer keeps the identity of every row it did not touch, so
 * an arriving message re-renders one row and not the window (REUSE C5).
 */
export const MessageRow = memo(MessageRowImpl)
